import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// The tools of the MCP Toolbox 1.14.0's prebuilt Looker config that agents
// see. Each description and input schema is the Toolbox's own, transcribed
// from its source and not yet checked against a real `tools/list`. The
// annotations are P0's: the Toolbox marks `query_url` read-only, but it saves a
// query.
//
// Left out: the LookML development tools, which no level can use; the
// `health_*` tools, which read System Activity across the whole instance;
// `generate_embed_url`, whose signed URL works like a session token; and
// `ask_data_insights` and the Conversational Analytics agent tools, which call
// Gemini as the container's Google Cloud identity rather than the grant's.
// The gateway only lists what is here. It doesn't refuse a call by another
// tool's name: the grant's Looker role does.
export const lookerAiMcpTools: McpTool[] = [
  {
    name: "get_models",
    description:
      "This tool retrieves a list of available LookML models in the Looker instance.\nLookML models define the data structure and relationships that users can query.\nThe output includes details like the model's `name` and `label`, which are\nessential for subsequent calls to tools like `get_explores` or `query`.\n\nThis tool takes no parameters.",
    inputSchema: {
      type: "object",
      properties: {},
      required: [],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_explores",
    description:
      "This tool retrieves a list of explores defined within a specific LookML model.\nExplores represent a curated view of your data, typically joining several\ntables together to allow for focused analysis on a particular subject area.\nThe output provides details like the explore's `name`, `label`, and whether it is `hidden`.\n\nParameters:\n- model_name (required): The name of the LookML model, obtained from `get_models`.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explores.",
        },
      },
      required: ["model"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_explore",
    description:
      "This tool retrieves detailed metadata about a specific Looker explore, including its\nconstraints and rules (e.g., `always_filter`, `conditionally_filter`, `tags`).\n\nCRITICAL: You MUST use this tool to inspect the explore's metadata BEFORE generating\nor running a query. If an explore has mandatory filters defined (like `always_filter`\nor `conditionally_filter`), your query will fail unless you include them.\n\nParameters:\n- model (required): The name of the LookML model, obtained from `get_models`.\n- explore (required): The name of the explore within the model, obtained from `get_explores`.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to get metadata for.",
        },
      },
      required: ["model", "explore"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_dimensions",
    description:
      "This tool retrieves a list of dimensions defined within a specific Looker explore.\nDimensions are non-aggregatable attributes or characteristics of your data\n(e.g., product name, order date, customer city) that can be used for grouping,\nfiltering, or segmenting query results.\n\nParameters:\n- model_name (required): The name of the LookML model, obtained from `get_models`.\n- explore_name (required): The name of the explore within the model, obtained from `get_explores`.\n\nOutput Details:\n- If a dimension includes a `suggestions` field, its contents are valid values\n  that can be used directly as filters for that dimension.\n- If a `suggest_explore` and `suggest_dimension` are provided, you can query\n  that specified explore and dimension to retrieve a list of valid filter values.\n- If a dimension includes `\"suggestable\": true`, you can retrieve its valid value suggestions by calling the 'get_field_value_suggestions' tool, passing the dimension's 'name' as the 'field' parameter, alongside the explore and model names.\n- If a dimension includes a `value_format` (an Excel style format string) or a\n  `value_format_name` (a named format such as `usd` or `percent_2`), it defines\n  how that dimension's values should be displayed. Format any values you report\n  for that dimension accordingly rather than showing the raw number, and use the\n  format to reason about the field's units.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore containing the fields.",
        },
      },
      required: ["model", "explore"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_measures",
    description:
      "This tool retrieves a list of measures defined within a specific Looker explore.\nMeasures are aggregatable metrics (e.g., total sales, average price, count of users)\nthat are used for calculations and quantitative analysis in your queries.\n\nParameters:\n- model_name (required): The name of the LookML model, obtained from `get_models`.\n- explore_name (required): The name of the explore within the model, obtained from `get_explores`.\n\nOutput Details:\n- If a measure includes a `suggestions` field, its contents are valid values\n  that can be used directly as filters for that measure.\n- If a `suggest_explore` and `suggest_dimension` are provided, you can query\n  that specified explore and dimension to retrieve a list of valid filter values.\n- If a measure includes a `value_format` (an Excel style format string) or a\n  `value_format_name` (a named format such as `usd` or `percent_2`), it defines\n  how that measure's values should be displayed. Format any values you report\n  for that measure accordingly rather than showing the raw number, and use the\n  format to reason about the measure's units when comparing or combining metrics.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore containing the fields.",
        },
      },
      required: ["model", "explore"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_filters",
    description:
      'This tool retrieves a list of "filter-only fields" defined within a specific\nLooker explore. These are special fields defined in LookML specifically to\ncreate user-facing filter controls that do not directly affect the `GROUP BY`\nclause of the SQL query. They are often used in conjunction with liquid templating\nto create dynamic queries.\n\nNote: Regular dimensions and measures can also be used as filters in a query.\nThis tool *only* returns fields explicitly defined as `filter:` in LookML.\n\nParameters:\n- model_name (required): The name of the LookML model, obtained from `get_models`.\n- explore_name (required): The name of the explore within the model, obtained from `get_explores`.',
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore containing the fields.",
        },
      },
      required: ["model", "explore"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_parameters",
    description:
      "This tool retrieves a list of parameters defined within a specific Looker explore.\nLookML parameters are dynamic input fields that allow users to influence query\nbehavior without directly modifying the underlying LookML. They are often used\nwith `liquid` templating to create flexible dashboards and reports, enabling\nusers to choose dimensions, measures, or other query components at runtime.\n\nParameters:\n- model_name (required): The name of the LookML model, obtained from `get_models`.\n- explore_name (required): The name of the explore within the model, obtained from `get_explores`.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore containing the fields.",
        },
      },
      required: ["model", "explore"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_field_value_suggestions",
    description:
      'This tool retrieves distinct value suggestions for a field, facilitating accurate filtering in downstream queries.\n\nRequired Parameters:\n- model: The name of the LookML model, obtained from `get_models`.\n- explore: The name of the explore containing the field, obtained from `get_explores`.\n- field: The name of the field to get suggestions for (obtained from a dimension where \'suggestable\' is true in the \'get_dimensions\' output).\n\nOptional Parameters:\n- term: Optional search term pattern to filter suggestions. Evaluated as `%term%`.\n- filters: Optional filters to enable conditional suggestions, represented as a map of field names to filter expressions, e.g., `{"users.state": "CA"}`.\n\nOutput:\n- A JSON object with a "suggestions" key containing an array of string values representing suggestions.',
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore containing the fields.",
        },
        field: {
          type: "string",
          description: "The name of the field to get suggestions for.",
        },
        term: {
          type: "string",
          description: "Optional search term pattern.",
        },
        filters: {
          type: "object",
          description:
            "Optional filters to enable conditional suggestions (restricting suggestions based on other field values).",
          default: {},
          additionalProperties: true,
        },
      },
      required: ["model", "explore", "field"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "query",
    description:
      'This tool runs a query against a LookML model and returns the results in JSON format.\n\nRequired Parameters:\n- model_name: The name of the LookML model (from `get_models`).\n- explore_name: The name of the explore (from `get_explores`).\n- fields: A list of field names (dimensions, measures, filters, or parameters) to include in the query.\n\nOptional Parameters:\n- pivots: A list of fields to pivot the results by. These fields must also be included in the `fields` list.\n- filters: A map of filter expressions, e.g., `{"view_name.field_name": "value", "view_name.date": "7 days"}`.\n  - Each key must be a fully-scoped `view_name.field_name`, copied verbatim from\n    `get_dimensions`, `get_measures`, `get_filters`, or `get_parameters`. The view\n    prefix and the dot are required.\n  - Each value is a Looker filter expression. Pass values bare: do not wrap them in\n    extra quote characters. For LookML `parameter` fields, use the raw allowed_value\n    (e.g. `first_touch`, not `"first_touch"`).\n  - Use `not null` instead of `-NULL`.\n  - If a value contains a comma, enclose it in single quotes (e.g., `\'New York, NY\'`).\n  - To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.\n- filter_expression: A Looker expression filter string (custom filter). This allows complex logic and comparing fields.\n  - Reference fields using `${view.field_name}` syntax.\n  - Supports logical operators (`AND`, `OR`, `NOT`) and comparison operators.\n  - Supports Looker functions (e.g., `matches_filter`, `now`, `add_days`, `diff_days`).\n  - Examples:\n    - `${orders.order_date} < add_years(-1, now())`\n    - `${activity.email} != ${activity_drive_facts.current_owner_email}`\n    - `matches_filter(${order.order_month}, \'24 months\') AND matches_filter(${order.order_month}, \'before 2024/07/01\')`\n- dynamic_fields: An optional array of dynamic fields (table calculations, custom measures, custom dimensions) defined as JSON objects.\n  - Useful for ad-hoc calculations that are not defined in the LookML model.\n  - Reference fields using `${view.field_name}` syntax.\n  - Examples:\n    - Table Calculation: `[{"table_calculation": "test", "label": "test", "expression": "${order_items.total_sale_price} * 0.8", "_type_hint": "number"}]`\n    - Custom Dimension: `[{"dimension": "days_since_order", "label": "days since order", "expression": "diff_days(${order.order_date}, now())", "_type_hint": "number"}]`\n    - Custom Measure: `[{"measure": "sum_of_revenue", "label": "Sum of Revenue", "based_on": "training.revenue", "type": "sum", "_type_hint": "number"}]`\n- sorts: A list of fields to sort by, optionally including direction (e.g., `["view.field desc"]`).\n- limit: Row limit (default 500). Use "-1" for unlimited.\n- query_timezone: specific timezone for the query (e.g. `America/Los_Angeles`).\n\nNote: Use `get_dimensions`, `get_measures`, `get_filters`, and `get_parameters` to find valid fields.',
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
      },
      required: ["model", "explore", "fields"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "query_sql",
    description:
      "This tool generates the underlying SQL query that Looker would execute\nagainst the database for a given set of parameters. It is useful for\nunderstanding how Looker translates a request into SQL.\n\nParameters:\nAll parameters for this tool are identical to those of the `query` tool.\nThis includes `model_name`, `explore_name`, `fields` (required),\nand optional parameters like `pivots`, `filters`, `filter_expression`, `dynamic_fields`, `sorts`, `limit`, and `query_timezone`.\n\nOutput:\nThe result of this tool is the raw SQL text.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
      },
      required: ["model", "explore", "fields"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "query_url",
    description:
      'This tool generates a shareable URL for a Looker query, allowing users to\nexplore the query further within the Looker UI. It returns the generated URL,\nalong with the `query_id` and `slug`.\n\nParameters:\nAll query parameters (e.g., `model_name`, `explore_name`, `fields`, `pivots`,\n`filters`, `filter_expression`, `dynamic_fields`, `sorts`, `limit`, `query_timezone`) are the same as the `query` tool.\n\nAdditionally, it accepts an optional `vis_config` parameter:\n- vis_config (optional): A JSON object that controls the default visualization\n  settings for the generated query.\n\nvis_config Details:\nThe `vis_config` object supports a wide range of properties for various chart types.\nHere are some notes on making visualizations.\n\n### Cartesian Charts (Area, Bar, Column, Line, Scatter)\n\nThese chart types share a large number of configuration options.\n\n**General**\n*   `type`: The type of visualization (`looker_area`, `looker_bar`, `looker_column`, `looker_line`, `looker_scatter`).\n*   `series_types`: Override the chart type for individual series.\n*   `show_view_names`: Display view names in labels and tooltips (`true`/`false`).\n*   `series_labels`: Provide custom names for series.\n\n**Styling & Colors**\n*   `colors`: An array of color values to be used for the chart series.\n*   `series_colors`: A mapping of series names to specific color values.\n*   `color_application`: Advanced controls for color palette application (collection, palette, reverse, etc.).\n*   `font_size`: Font size for labels (e.g., \'12px\').\n\n**Legend**\n*   `hide_legend`: Show or hide the chart legend (`true`/`false`).\n*   `legend_position`: Placement of the legend (`\'center\'`, `\'left\'`, `\'right\'`).\n\n**Axes**\n*   `swap_axes`: Swap the X and Y axes (`true`/`false`).\n*   `x_axis_scale`: Scale of the x-axis (`\'auto\'`, `\'ordinal\'`, `\'linear\'`, `\'time\'`).\n*   `x_axis_reversed`, `y_axis_reversed`: Reverse the direction of an axis (`true`/`false`).\n*   `x_axis_gridlines`, `y_axis_gridlines`: Display gridlines for an axis (`true`/`false`).\n*   `show_x_axis_label`, `show_y_axis_label`: Show or hide the axis title (`true`/`false`).\n*   `show_x_axis_ticks`, `show_y_axis_ticks`: Show or hide axis tick marks (`true`/`false`).\n*   `x_axis_label`, `y_axis_label`: Set a custom title for an axis.\n*   `x_axis_datetime_label`: A format string for datetime labels on the x-axis (e.g., `\'%Y-%m\'`).\n*   `x_padding_left`, `x_padding_right`: Adjust padding on the ends of the x-axis.\n*   `x_axis_label_rotation`, `x_axis_label_rotation_bar`: Set rotation for x-axis labels.\n*   `x_axis_zoom`, `y_axis_zoom`: Enable zooming on an axis (`true`/`false`).\n*   `y_axes`: An array of configuration objects for multiple y-axes.\n\n**Data & Series**\n*   `stacking`: How to stack series (`\'\'` for none, `\'normal\'`, `\'percent\'`).\n*   `ordering`: Order of series in a stack (`\'none\'`, etc.).\n*   `limit_displayed_rows`: Enable or disable limiting the number of rows displayed (`true`/`false`).\n*   `limit_displayed_rows_values`: Configuration for the row limit (e.g., `{ "first_last": "first", "show_hide": "show", "num_rows": 10 }`).\n*   `discontinuous_nulls`: How to render null values in line charts (`true`/`false`).\n*   `point_style`: Style for points on line and area charts (`\'none\'`, `\'circle\'`, `\'circle_outline\'`).\n*   `series_point_styles`: Override point styles for individual series.\n*   `interpolation`: Line interpolation style (`\'linear\'`, `\'monotone\'`, `\'step\'`, etc.).\n*   `show_value_labels`: Display values on data points (`true`/`false`).\n*   `label_value_format`: A format string for value labels.\n*   `show_totals_labels`: Display total labels on stacked charts (`true`/`false`).\n*   `totals_color`: Color for total labels.\n*   `show_silhouette`: Display a "silhouette" of hidden series in stacked charts (`true`/`false`).\n*   `hidden_series`: An array of series names to hide from the visualization.\n\n**Scatter/Bubble Specific**\n*   `size_by_field`: The field used to determine the size of bubbles.\n*   `color_by_field`: The field used to determine the color of bubbles.\n*   `plot_size_by_field`: Whether to display the size-by field in the legend.\n*   `cluster_points`: Group nearby points into clusters (`true`/`false`).\n*   `quadrants_enabled`: Display quadrants on the chart (`true`/`false`).\n*   `quadrant_properties`: Configuration for quadrant labels and colors.\n*   `custom_quadrant_value_x`, `custom_quadrant_value_y`: Set quadrant boundaries as a percentage.\n*   `custom_quadrant_point_x`, `custom_quadrant_point_y`: Set quadrant boundaries to a specific value.\n\n**Miscellaneous**\n*   `reference_lines`: Configuration for displaying reference lines.\n*   `trend_lines`: Configuration for displaying trend lines.\n*   `trellis`: Configuration for creating trellis (small multiple) charts.\n*   `crossfilterEnabled`, `crossfilters`: Configuration for cross-filtering interactions.\n\n### Boxplot\n\n*   Inherits most of the Cartesian chart options.\n*   `type`: Must be `looker_boxplot`.\n\n### Funnel\n\n*   `type`: Must be `looker_funnel`.\n*   `orientation`: How data is read (`\'automatic\'`, `\'dataInRows\'`, `\'dataInColumns\'`).\n*   `percentType`: How percentages are calculated (`\'percentOfMaxValue\'`, `\'percentOfPriorRow\'`).\n*   `labelPosition`, `valuePosition`, `percentPosition`: Placement of labels (`\'left\'`, `\'right\'`, `\'inline\'`, `\'hidden\'`).\n*   `labelColor`, `labelColorEnabled`: Set a custom color for labels.\n*   `labelOverlap`: Allow labels to overlap (`true`/`false`).\n*   `barColors`: An array of colors for the funnel steps.\n*   `color_application`: Advanced color palette controls.\n*   `crossfilterEnabled`, `crossfilters`: Configuration for cross-filtering.\n\n### Pie / Donut\n\n*   Pie charts must have exactly one dimension and one numerical measure.\n*   `type`: Must be `looker_pie`.\n*   `value_labels`: Where to display values (`\'legend\'`, `\'labels\'`).\n*   `label_type`: The format of data labels (`\'labPer\'`, `\'labVal\'`, `\'lab\'`, `\'val\'`, `\'per\'`).\n*   `start_angle`, `end_angle`: The start and end angles of the pie chart.\n*   `inner_radius`: The inner radius, used to create a donut chart.\n*   `series_colors`, `series_labels`: Override colors and labels for specific slices.\n*   `color_application`: Advanced color palette controls.\n*   `crossfilterEnabled`, `crossfilters`: Configuration for cross-filtering.\n*   `advanced_vis_config`: A string containing JSON for advanced Highcharts configuration.\n\n### Waterfall\n\n*   Inherits most of the Cartesian chart options.\n*   `type`: Must be `looker_waterfall`.\n*   `up_color`: Color for positive (increasing) values.\n*   `down_color`: Color for negative (decreasing) values.\n*   `total_color`: Color for the total bar.\n\n### Word Cloud\n\n*   `type`: Must be `looker_wordcloud`.\n*   `rotation`: Enable random word rotation (`true`/`false`).\n*   `colors`: An array of colors for the words.\n*   `color_application`: Advanced color palette controls.\n*   `crossfilterEnabled`, `crossfilters`: Configuration for cross-filtering.\n\nThese are some sample vis_config settings.\n\nA bar chart -\n{{\n  "defaults_version": 1,\n  "label_density": 25,\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "ordering": "none",\n  "plot_size_by_field": false,\n  "point_style": "none",\n  "show_null_labels": false,\n  "show_silhouette": false,\n  "show_totals_labels": false,\n  "show_value_labels": false,\n  "show_view_names": false,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "stacking": "normal",\n  "totals_color": "#808080",\n  "trellis": "",\n  "type": "looker_bar",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "x_axis_zoom": true,\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5,\n  "y_axis_zoom": true\n}}\n\nA column chart with an option advanced_vis_config -\n{{\n  "advanced_vis_config": "{ chart: { type: \'pie\', spacingBottom: 50, spacingLeft: 50, spacingRight: 50, spacingTop: 50, }, legend: { enabled: false, }, plotOptions: { pie: { dataLabels: { enabled: true, format: \'\\\\u003cb\\\\u003e{key}\\\\u003c/b\\\\u003e\\\\u003cspan style=\\"font-weight: normal\\"\\\\u003e - {percentage:.2f}%\\\\u003c/span\\\\u003e\', }, showInLegend: false, }, }, series: [], }",\n  "colors": [\n    "grey"\n  ],\n  "defaults_version": 1,\n  "hidden_fields": [],\n  "label_density": 25,\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "note_display": "below",\n  "note_state": "collapsed",\n  "note_text": "Unsold inventory only",\n  "ordering": "none",\n  "plot_size_by_field": false,\n  "point_style": "none",\n  "series_colors": {},\n  "show_null_labels": false,\n  "show_silhouette": false,\n  "show_totals_labels": false,\n  "show_value_labels": true,\n  "show_view_names": false,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "stacking": "normal",\n  "totals_color": "#808080",\n  "trellis": "",\n  "type": "looker_column",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "x_axis_zoom": true,\n  "y_axes": [],\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5,\n  "y_axis_zoom": true\n}}\n\nA line chart -\n{{\n  "defaults_version": 1,\n  "hidden_pivots": {},\n  "hidden_series": [],\n  "interpolation": "linear",\n  "label_density": 25,\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "plot_size_by_field": false,\n  "point_style": "none",\n  "series_types": {},\n  "show_null_points": true,\n  "show_value_labels": false,\n  "show_view_names": false,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "stacking": "",\n  "trellis": "",\n  "type": "looker_line",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5\n}}\n\nAn area chart -\n{{\n  "defaults_version": 1,\n  "interpolation": "linear",\n  "label_density": 25,\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "plot_size_by_field": false,\n  "point_style": "none",\n  "series_types": {},\n  "show_null_points": true,\n  "show_silhouette": false,\n  "show_totals_labels": false,\n  "show_value_labels": false,\n  "show_view_names": false,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "stacking": "normal",\n  "totals_color": "#808080",\n  "trellis": "",\n  "type": "looker_area",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "x_axis_zoom": true,\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5,\n  "y_axis_zoom": true\n}}\n\nA scatter plot -\n{{\n  "cluster_points": false,\n  "custom_quadrant_point_x": 5,\n  "custom_quadrant_point_y": 5,\n  "custom_value_label_column": "",\n  "custom_x_column": "",\n  "custom_y_column": "",\n  "defaults_version": 1,\n  "hidden_fields": [],\n  "hidden_pivots": {},\n  "hidden_points_if_no": [],\n  "hidden_series": [],\n  "interpolation": "linear",\n  "label_density": 25,\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "limit_displayed_rows_values": {\n    "first_last": "first",\n    "num_rows": 0,\n    "show_hide": "hide"\n  },\n  "plot_size_by_field": false,\n  "point_style": "circle",\n  "quadrant_properties": {\n    "0": {\n      "color": "",\n      "label": "Quadrant 1"\n    },\n    "1": {\n      "color": "",\n      "label": "Quadrant 2"\n    },\n    "2": {\n      "color": "",\n      "label": "Quadrant 3"\n    },\n    "3": {\n      "color": "",\n      "label": "Quadrant 4"\n    }\n  },\n  "quadrants_enabled": false,\n  "series_labels": {},\n  "series_types": {},\n  "show_null_points": false,\n  "show_value_labels": false,\n  "show_view_names": true,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "size_by_field": "roi",\n  "stacking": "normal",\n  "swap_axes": true,\n  "trellis": "",\n  "type": "looker_scatter",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "x_axis_zoom": true,\n  "y_axes": [\n    {\n      "label": "",\n      "orientation": "bottom",\n      "series": [\n        {\n          "axisId": "Channel_0 - average_of_roi_first",\n          "id": "Channel_0 - average_of_roi_first",\n          "name": "Channel_0"\n        },\n        {\n          "axisId": "Channel_1 - average_of_roi_first",\n          "id": "Channel_1 - average_of_roi_first",\n          "name": "Channel_1"\n        },\n        {\n          "axisId": "Channel_2 - average_of_roi_first",\n          "id": "Channel_2 - average_of_roi_first",\n          "name": "Channel_2"\n        },\n        {\n          "axisId": "Channel_3 - average_of_roi_first",\n          "id": "Channel_3 - average_of_roi_first",\n          "name": "Channel_3"\n        },\n        {\n          "axisId": "Channel_4 - average_of_roi_first",\n          "id": "Channel_4 - average_of_roi_first",\n          "name": "Channel_4"\n        }\n      ],\n      "showLabels": true,\n      "showValues": true,\n      "tickDensity": "custom",\n      "tickDensityCustom": 100,\n      "type": "linear",\n      "unpinAxis": false\n    }\n  ],\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5,\n  "y_axis_zoom": true\n}}\n\nA single record visualization -\n{{\n  "defaults_version": 1,\n  "show_view_names": false,\n  "type": "looker_single_record"\n}}\n\nA single value visualization -\n{{\n  "comparison_reverse_colors": false,\n  "comparison_type": "value",                                                                                                                                            "conditional_formatting_include_nulls": false,                                                                                                                         "conditional_formatting_include_totals": false,\n  "custom_color": "#1A73E8",\n  "custom_color_enabled": true,\n  "defaults_version": 1,\n  "enable_conditional_formatting": false,\n  "series_types": {},\n  "show_comparison": false,\n  "show_comparison_label": true,\n  "show_single_value_title": true,\n  "single_value_title": "Total Clicks",\n  "type": "single_value"\n}}\n\nA Pie chart -\n{{\n  "defaults_version": 1,\n  "label_density": 25,\n  "label_type": "labPer",\n  "legend_position": "center",\n  "limit_displayed_rows": false,\n  "ordering": "none",\n  "plot_size_by_field": false,\n  "point_style": "none",\n  "series_types": {},\n  "show_null_labels": false,\n  "show_silhouette": false,\n  "show_totals_labels": false,\n  "show_value_labels": false,\n  "show_view_names": false,\n  "show_x_axis_label": true,\n  "show_x_axis_ticks": true,\n  "show_y_axis_labels": true,\n  "show_y_axis_ticks": true,\n  "stacking": "",\n  "totals_color": "#808080",\n  "trellis": "",\n  "type": "looker_pie",\n  "value_labels": "legend",\n  "x_axis_gridlines": false,\n  "x_axis_reversed": false,\n  "x_axis_scale": "auto",\n  "y_axis_combined": true,\n  "y_axis_gridlines": true,\n  "y_axis_reversed": false,\n  "y_axis_scale_mode": "linear",\n  "y_axis_tick_density": "default",\n  "y_axis_tick_density_custom": 5\n}}\n\nThe result is a JSON object with the id, slug, the url, and\nthe long_url.',
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
        vis_config: {
          type: "object",
          description: "The visualization config for the query",
          default: {},
          additionalProperties: true,
        },
      },
      required: ["model", "explore", "fields"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_looks",
    description:
      'This tool searches for saved Looks (pre-defined queries and visualizations)\nin a Looker instance. It returns a list of JSON objects, each representing a Look, including its id, title, description, model_id, and certification_metadata (when certified or revoked).\n\nSearch Parameters:\n- title (optional): Filter by Look title (supports wildcards).\n- folder_id (optional): Filter by the ID of the folder where the Look is saved.\n- user_id (optional): Filter by the ID of the user who created the Look.\n- description (optional): Filter by description content (supports wildcards).\n- id (optional): Filter by specific Look ID.\n- limit (optional): Maximum number of results to return. Defaults to a system limit.\n- offset (optional): Starting point for pagination.\n\nString Search Behavior:\n- Case-insensitive matching.\n- Supports SQL LIKE pattern match wildcards:\n  - `%`: Matches any sequence of zero or more characters. (e.g., `"dan%"` matches "danger", "Danzig")\n  - `_`: Matches any single character. (e.g., `"D_m%"` matches "Damage", "dump")\n- Special expressions for null checks:\n  - `"IS NULL"`: Matches Looks where the field is null.\n  - `"NOT NULL"`: Excludes Looks where the field is null.',
    inputSchema: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "The title of the look.",
          default: "",
        },
        desc: {
          type: "string",
          description: "The description of the look.",
          default: "",
        },
        limit: {
          type: "integer",
          description: "The number of looks to fetch. Default 100",
          default: 100,
        },
        offset: {
          type: "integer",
          description: "The number of looks to skip before fetching. Default 0",
          default: 0,
        },
      },
      required: [],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "run_look",
    description:
      "This tool executes the query associated with a saved Look and\nreturns the resulting data in a JSON structure.\n\nParameters:\n- look_id (required): The unique identifier of the Look to run,\n  typically obtained from the `get_looks` tool.\n\nOutput:\nThe query results are returned as a JSON object.",
    inputSchema: {
      type: "object",
      properties: {
        look_id: {
          type: "string",
          description: "The id of the look to run.",
        },
        limit: {
          type: "integer",
          description: "The row limit. Default 500",
          default: 500,
        },
      },
      required: ["look_id"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_dashboards",
    description:
      'This tool searches for saved dashboards in a Looker instance. It returns a list of JSON objects, each representing a dashboard, including its id, title, description, and certification_metadata (when certified or revoked).\n\nSearch Parameters:\n- title (optional): Filter by dashboard title (supports wildcards).\n- folder_id (optional): Filter by the ID of the folder where the dashboard is saved.\n- user_id (optional): Filter by the ID of the user who created the dashboard.\n- description (optional): Filter by description content (supports wildcards).\n- id (optional): Filter by specific dashboard ID.\n- limit (optional): Maximum number of results to return. Defaults to a system limit.\n- offset (optional): Starting point for pagination.\n\nString Search Behavior:\n- Case-insensitive matching.\n- Supports SQL LIKE pattern match wildcards:\n  - `%`: Matches any sequence of zero or more characters. (e.g., `"finan%"` matches "financial", "finance")\n  - `_`: Matches any single character. (e.g., `"s_les"` matches "sales")\n- Special expressions for null checks:\n  - `"IS NULL"`: Matches dashboards where the field is null.\n  - `"NOT NULL"`: Excludes dashboards where the field is null.',
    inputSchema: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "The title of the dashboard.",
          default: "",
        },
        desc: {
          type: "string",
          description: "The description of the dashboard.",
          default: "",
        },
        limit: {
          type: "integer",
          description: "The number of dashboards to fetch. Default 100",
          default: 100,
        },
        offset: {
          type: "integer",
          description:
            "The number of dashboards to skip before fetching. Default 0",
          default: 0,
        },
      },
      required: [],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "get_dashboard",
    description:
      "This tool retrieves the JSON design of a Looker dashboard by its ID.\nThe output is trimmed to essential fields (including certification_metadata), excluding alerts and scheduled plans,\nmaking it suitable for replicating or analyzing the dashboard structure.\n\nParameters:\n- dashboard_id (required): The unique identifier of the dashboard.",
    inputSchema: {
      type: "object",
      properties: {
        dashboard_id: {
          type: "string",
          description: "The id of the dashboard to retrieve.",
        },
      },
      required: ["dashboard_id"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "run_dashboard",
    description:
      "This tool executes the queries associated with each tile in a specified dashboard\nand returns the aggregated data in a JSON structure.\n\nParameters:\n- dashboard_id (required): The unique identifier of the dashboard to run,\n  typically obtained from the `get_dashboards` tool.\n\nOutput:\nThe data from all dashboard tiles is returned as a JSON object.",
    inputSchema: {
      type: "object",
      properties: {
        dashboard_id: {
          type: "string",
          description: "The id of the dashboard to run.",
        },
      },
      required: ["dashboard_id"],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "make_look",
    description:
      "This tool creates a new Look (saved query with visualization) in Looker.\nThe Look will be saved in the user's personal folder, and its name must be unique.\n\nRequired Parameters:\n- title: A unique title for the new Look.\n- description: A brief description of the Look's purpose.\n- model_name: The name of the LookML model (from `get_models`).\n- explore_name: The name of the explore (from `get_explores`).\n- fields: A list of field names (dimensions, measures, filters, or parameters) to include in the query.\n\nOptional Parameters:\n- pivots, filters, sorts, limit, query_timezone: These parameters are identical\n  to those described for the `query` tool.\n- vis_config: A JSON object defining the visualization settings for the Look.\n  The structure and options are the same as for the `query_url` tool's `vis_config`.\n\nOutput:\nA JSON object containing a link (`url`) to the newly created Look, along with its `id` and `slug`.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
        title: {
          type: "string",
          description: "The title of the Look",
        },
        description: {
          type: "string",
          description: "The description of the Look",
          default: "",
        },
        folder: {
          type: "string",
          description:
            "The folder id where the Look will be created. Leave blank to use the user's personal folder",
          default: "",
        },
        vis_config: {
          type: "object",
          description: "The visualization config for the query",
          default: {},
          additionalProperties: true,
        },
      },
      required: ["model", "explore", "fields", "title"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "make_dashboard",
    description:
      "This tool creates a new, empty dashboard in Looker. Dashboards are stored\nin the user's personal folder, and the dashboard name must be unique.\nAfter creation, use `add_dashboard_filter` to add filters and\n`add_dashboard_element` to add content tiles.\n\nRequired Parameters:\n- title (required): A unique title for the new dashboard.\n- description (required): A brief description of the dashboard's purpose.\n\nOutput:\nA JSON object containing a link (`url`) to the newly created dashboard and\nits unique `id`. This `dashboard_id` is crucial for subsequent calls to\n`add_dashboard_filter` and `add_dashboard_element`.",
    inputSchema: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "The title of the Dashboard",
        },
        description: {
          type: "string",
          description: "The description of the Dashboard",
          default: "",
        },
        folder: {
          type: "string",
          description:
            "The folder id where the Dashboard will be created. Leave blank to use the user's personal folder",
          default: "",
        },
      },
      required: ["title"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "add_dashboard_element",
    description:
      "This tool creates a new tile (element) within an existing Looker dashboard.\nTiles are added in the order this tool is called for a given `dashboard_id`.\n\nCRITICAL ORDER OF OPERATIONS:\n1. Create the dashboard using `make_dashboard`.\n2. Add any dashboard-level filters using `add_dashboard_filter`.\n3. Then, add elements (tiles) using this tool.\n\nRequired Parameters:\n- dashboard_id: The ID of the target dashboard, obtained from `make_dashboard`.\n- model_name, explore_name, fields: These query parameters are inherited\n  from the `query` tool and are required to define the data for the tile.\n\nOptional Parameters:\n- title: An optional title for the dashboard tile.\n- pivots, filters, sorts, limit, query_timezone: These query parameters are\n  inherited from the `query` tool and can be used to customize the tile's query.\n- vis_config: A JSON object defining the visualization settings for this tile.\n  The structure and options are the same as for the `query_url` tool's `vis_config`.\n\nConnecting to Dashboard Filters:\nA dashboard element can be connected to one or more dashboard filters (created with\n`add_dashboard_filter`). To do this, specify the `name` of the dashboard filter\nand the `field` from the element's query that the filter should apply to.\nThe format for specifying the field is `view_name.field_name`.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
        dashboard_id: {
          type: "string",
          description: "The id of the dashboard where this tile will exist",
        },
        title: {
          type: "string",
          description: "The title of the Dashboard Element",
          default: "",
        },
        vis_config: {
          type: "object",
          description: "The visualization config for the query",
          default: {},
          additionalProperties: true,
        },
        dashboard_filters: {
          type: "array",
          description:
            'An array of dashboard filters like [{"dashboard_filter_name": "name", "field": "view_name.field_name"}, ...]',
          items: {
            type: "object",
            description:
              'A dashboard filter like {"dashboard_filter_name": "name", "field": "view_name.field_name"}',
            additionalProperties: true,
          },
        },
      },
      required: ["model", "explore", "fields", "dashboard_id"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "add_dashboard_filter",
    description:
      "This tool adds a filter to a Looker dashboard.\n\nCRITICAL ORDER OF OPERATIONS:\n1. Create a dashboard using `make_dashboard`.\n2. Add all desired filters using this tool (`add_dashboard_filter`).\n3. Finally, add dashboard elements (tiles) using `add_dashboard_element`.\n\nParameters:\n- dashboard_id (required): The ID from `make_dashboard`.\n- name (required): A unique internal identifier for the filter. You will use this `name` later in `add_dashboard_element` to bind tiles to this filter.\n- title (required): The label displayed to users in the UI.\n- filter_type (required): One of `date_filter`, `number_filter`, `string_filter`, or `field_filter`.\n- default_value (optional): The initial value for the filter.\n\nField Filters (`filter_type: field_filter`):\nIf creating a field filter, you must also provide:\n- model\n- explore\n- dimension\nThe filter will inherit suggestions and type information from this LookML field.",
    inputSchema: {
      type: "object",
      properties: {
        dashboard_id: {
          type: "string",
          description: "The id of the dashboard where this filter will exist",
        },
        name: {
          type: "string",
          description: "The name of the Dashboard Filter",
        },
        title: {
          type: "string",
          description: "The title of the Dashboard Filter",
        },
        filter_type: {
          type: "string",
          description:
            "The filter_type of the Dashboard Filter: date_filter, number_filter, string_filter, field_filter (default field_filter)",
          default: "field_filter",
        },
        default_value: {
          type: "string",
          description: "The default_value of the Dashboard Filter (optional)",
        },
        model: {
          type: "string",
          description:
            "The model of a field type Dashboard Filter (required if type field)",
        },
        explore: {
          type: "string",
          description:
            "The explore of a field type Dashboard Filter (required if type field)",
        },
        dimension: {
          type: "string",
          description:
            "The dimension of a field type Dashboard Filter (required if type field)",
        },
        allow_multiple_values: {
          type: "boolean",
          description:
            "The Dashboard Filter should allow multiple values (default true)",
          default: true,
        },
        required: {
          type: "boolean",
          description:
            "The Dashboard Filter is required to run dashboard (default false)",
          default: false,
        },
      },
      required: ["dashboard_id", "name", "title"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "update_dashboard_element",
    description:
      "This tool updates an existing query element (tile) in a Looker dashboard.\nIt reconstructs the query for the element using the provided parameters\nand updates its title and visualization configuration.\n\nRequired Parameters:\n- dashboard_id: The ID of the dashboard containing the element.\n- dashboard_element_id: The ID of the element to update.\n- model, explore, fields: These query parameters define the data for the tile.\n\nOptional Parameters:\n- title: The new title for the dashboard tile.\n- pivots, filters, sorts, limit, tz: These query parameters customize the tile's query.\n- vis_config: A JSON object defining the visualization settings.\n- dashboard_filters: An array of dashboard filters to connect to this element.",
    inputSchema: {
      type: "object",
      properties: {
        model: {
          type: "string",
          description: "The model containing the explore.",
        },
        explore: {
          type: "string",
          description: "The explore to be queried.",
        },
        fields: {
          type: "array",
          description: "The fields to be retrieved.",
          items: {
            type: "string",
            description: "A field to be returned in the query",
          },
        },
        filters: {
          type: "object",
          description:
            'The filters for the query. Keys are fully-qualified field names (e.g. "view.field") and values are filter expressions or parameter values. Pass values bare — do not wrap them in extra quote characters. For LookML `parameter` fields, use the raw allowed_value (e.g. `first_touch`), not `"first_touch"`. To retrieve valid filter values for a suggestible field, use the \'get_field_value_suggestions\' tool.',
          default: {},
          additionalProperties: true,
        },
        pivots: {
          type: "array",
          description: "The query pivots (must be included in fields as well).",
          items: {
            type: "string",
            description: "A field to be used as a pivot in the query",
          },
          default: [],
        },
        sorts: {
          type: "array",
          description: 'The sorts like "field.id desc 0".',
          items: {
            type: "string",
            description: "A field to be used as a sort in the query",
          },
          default: [],
        },
        limit: {
          type: "integer",
          description: "The row limit.",
          default: 500,
        },
        tz: {
          type: "string",
          description: "The query timezone.",
        },
        filter_expression: {
          type: "string",
          description: "An optional filter expression string.",
        },
        dynamic_fields: {
          type: "array",
          description:
            "An optional array of dynamic fields (table calculations, custom measures, custom dimensions).",
          items: {
            type: "object",
            description: "A dynamic field definition",
            additionalProperties: true,
          },
          default: [],
        },
        dashboard_id: {
          type: "string",
          description: "The ID of the dashboard containing the element.",
        },
        dashboard_element_id: {
          type: "string",
          description: "The ID of the dashboard element to update.",
        },
        title: {
          type: "string",
          description: "The new title of the element.",
          default: "",
        },
        vis_config: {
          type: "object",
          description: "The new visualization configuration.",
          default: {},
          additionalProperties: true,
        },
        dashboard_filters: {
          type: "array",
          description:
            'An array of dashboard filters like [{"dashboard_filter_name": "name", "field": "view_name.field_name"}, ...]',
          items: {
            type: "object",
            description:
              'A dashboard filter like {"dashboard_filter_name": "name", "field": "view_name.field_name"}',
            additionalProperties: true,
          },
        },
      },
      required: [
        "model",
        "explore",
        "fields",
        "dashboard_id",
        "dashboard_element_id",
      ],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
  {
    name: "create_dashboard_layout",
    description:
      "This tool creates a new dashboard layout, which typically represents a tab\nin modern Looker dashboards.\n\nParameters:\n- dashboard_id (required): The ID of the dashboard.\n- label (required): The label (title) of the new tab.\n- type (optional): The type of layout (defaults to 'newspaper').\n- active (optional): Whether to make this layout active.",
    inputSchema: {
      type: "object",
      properties: {
        dashboard_id: {
          type: "string",
          description: "The ID of the dashboard to create the layout for.",
        },
        label: {
          type: "string",
          description: "The label (title) of the layout/tab.",
        },
        type: {
          type: "string",
          description:
            "The type of layout (e.g. 'newspaper', 'grid'). Defaults to 'newspaper'.",
          default: "newspaper",
        },
        active: {
          type: "boolean",
          description: "Whether this layout should be active.",
          default: false,
        },
      },
      required: ["dashboard_id", "label"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: false,
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  {
    name: "update_dashboard_layout_component",
    description:
      "This tool updates a dashboard layout component, allowing you to move a tile\nto a different tab (layout) and/or change its position and size.\n\nParameters:\n- dashboard_layout_component_id (required): The ID of the component to update.\n- dashboard_layout_id (optional): The ID of the target layout (tab) to move to.\n- row (optional): The new row position.\n- column (optional): The new column position.\n- width (optional): The new width.\n- height (optional): The new height.",
    inputSchema: {
      type: "object",
      properties: {
        dashboard_layout_component_id: {
          type: "string",
          description: "The ID of the dashboard layout component to update.",
        },
        dashboard_layout_id: {
          type: "string",
          description:
            "The ID of the target dashboard layout (tab) to move the component to.",
        },
        row: {
          type: "integer",
          description: "The new row position.",
        },
        column: {
          type: "integer",
          description: "The new column position.",
        },
        width: {
          type: "integer",
          description: "The new width.",
        },
        height: {
          type: "integer",
          description: "The new height.",
        },
      },
      required: ["dashboard_layout_component_id"],
    },
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true,
      openWorldHint: false,
    },
  },
];
